'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import Image from 'next/image';

export default function DashboardPreview() {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // After mounting, we can safely show the UI that depends on the theme
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <motion.div
      className="w-full max-w-5xl mx-auto mt-12 md:mt-24 px-4 md:px-0 relative"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.6 }}
    >
      <div className="relative rounded-xl md:rounded-2xl overflow-hidden border border-stone-200/50 dark:border-stone-800/50 shadow-xl md:shadow-2xl bg-gradient-to-br from-white to-stone-50 dark:from-stone-900 dark:to-stone-950">
        <div className="bg-stone-100 dark:bg-stone-900 p-2 md:p-3 border-b border-stone-200 dark:border-stone-800 flex items-center">
          <div className="flex space-x-1.5 md:space-x-2">
            <div className="size-2 md:size-3 rounded-full bg-stone-300 dark:bg-stone-700" />
            <div className="size-2 md:size-3 rounded-full bg-stone-300 dark:bg-stone-700" />
            <div className="size-2 md:size-3 rounded-full bg-stone-300 dark:bg-stone-700" />
          </div>
          <div className="mx-auto text-xs md:text-sm text-stone-500 dark:text-stone-400">
            StockPenguins AI Dashboard
          </div>
        </div>

        {/* Dashboard image that changes based on theme with maintained aspect ratio */}
        <div className="bg-white dark:bg-stone-900 relative w-full">
          {/* Aspect ratio container - maintains 3456:1986 ratio (approximately 1.74:1) */}
          <div className="relative w-full" style={{ paddingBottom: '57.47%' }}>
            {mounted ? (
              <Image
                src={
                  resolvedTheme === 'dark'
                    ? 'https://d4drpytggsxye.cloudfront.net/thumbnails/dashboard-dark.png'
                    : 'https://d4drpytggsxye.cloudfront.net/thumbnails/dashboard-light.png'
                }
                alt="AI Dashboard Preview"
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 75vw, 1728px"
                className="object-contain"
                priority
              />
            ) : (
              <div className="absolute inset-0 animate-pulse bg-stone-100 dark:bg-stone-800" />
            )}
          </div>
        </div>
      </div>

      {/* Purple glow effect under the mockup */}
      <div
        className="absolute -bottom-6 md:-bottom-10 left-1/2 -translate-x-1/2 w-[90%] md:w-4/5 h-12 md:h-20 opacity-30 blur-[30px] md:blur-[50px]"
        style={{
          background:
            'linear-gradient(to right, rgba(77, 46, 245, 0.3), rgba(125, 81, 237, 0.5), rgba(77, 46, 245, 0.3))',
        }}
      />
    </motion.div>
  );
}
